/**
 * TaskChecklist — right-side execution drawer for AI-driven multi-step plans.
 *
 * The conversation lives in the user's own AI client (Claude, ChatGPT,
 * Cursor, …) connected over MCP; this drawer is the live view of the plan
 * that client proposed, ticking off steps as work executes. Approval
 * happens in the AI client, not here.
 *
 * Spec from FB-MOUBAQPD + FB-MOUBC54H + FB-MOTB1RJ7:
 *
 *   On propose_plan
 *     - slide in from the right
 *     - show plan title, "Affects: …" scope summary, entities chips
 *     - each step renders as a pending marker
 *     - "Approved at HH:MM" badge stamped when the user confirms
 *
 *   During execution (update_plan_step events)
 *     - per-step state icons: pending → running (spinner) → done (check) / failed (x)
 *     - subtle progress bar fills at the panel header
 *     - panel persists across page navigation (it's mounted in Layout)
 *
 *   On completion
 *     - all steps green, progress full, "View changes" CTA
 *     - panel stays open ~30s then auto-collapses to a small right-edge
 *       tab showing "Plan complete — N steps"
 *     - clicking the tab re-expands
 *
 * Source of events:
 *   - propose_plan and update_plan_step arrive via BrowserBus SSE when the
 *     external MCP client calls them. useBrowserBus fires a window
 *     CustomEvent (see plan-events.ts) and this drawer listens for it.
 *
 * State persistence: sessionStorage so a hard reload mid-execution
 * keeps the visible state (the AI client keeps firing update_plan_step
 * events as it works). Localstorage isn't used —
 * plans don't outlive the browser session.
 */
import { useEffect, useRef, useState } from "react";
import { Ban, Check, Dot, Loader2, X, type LucideIcon } from "lucide-react";

// Event contract lives in a sibling module so non-React surfaces
// (useBrowserBus) can fire plan events without taking
// a dep on this component. Re-exported below for back-compat with
// consumers that imported the helpers from this file historically.
import {
  PLAN_EVENT,
  dispatchPlanProposed,
  dispatchPlanStepUpdate,
  dispatchPlanApproved,
  type PlanEntity,
  type PlanProposal,
  type PlanStepUpdate,
  type PlanEventDetail,
} from "./plan-events.js";

export {
  PLAN_EVENT,
  dispatchPlanProposed,
  dispatchPlanStepUpdate,
  dispatchPlanApproved,
};
export type { PlanEntity, PlanProposal, PlanStepUpdate };

// ── Internal state shape ─────────────────────────────────────────────

interface PlanStepState {
  index: number;
  label: string;
  status: PlanStepUpdate["status"];
  resultSummary?: string;
  startedAt?: number;
  endedAt?: number;
}

interface PlanState {
  id: string;
  title: string;
  affects?: string;
  reversible?: boolean;
  shape?: string;
  entities?: PlanEntity[];
  steps: PlanStepState[];
  proposedAt: number;
  approvedAt?: number;
  completedAt?: number;
}

const DEFAULT_STORAGE_NAMESPACE = "cmcp";
const AUTO_COLLAPSE_DELAY_MS = 30_000;

function plansKey(ns: string) {
  return `${ns}:active-plans`;
}

function mutedKey(ns: string) {
  return `${ns}:checklist-muted`;
}

function loadPlans(ns: string): PlanState[] {
  try {
    const raw = sessionStorage.getItem(plansKey(ns));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function savePlans(ns: string, plans: PlanState[]) {
  try { sessionStorage.setItem(plansKey(ns), JSON.stringify(plans)); } catch { /* ignore */ }
}

function genId() {
  return `plan_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

function isComplete(plan: PlanState): boolean {
  if (plan.steps.length === 0) return false;
  return plan.steps.every((s) => s.status === "success" || s.status === "failed" || s.status === "skipped");
}

function planProgress(plan: PlanState): { done: number; total: number; pct: number } {
  const total = plan.steps.length;
  const done = plan.steps.filter((s) => s.status === "success" || s.status === "skipped").length;
  return { done, total, pct: total > 0 ? Math.round((done / total) * 100) : 0 };
}

// ── Component ─────────────────────────────────────────────────────────

export interface TaskChecklistProps {
  /**
   * Storage key namespace for persisted plans + mute pref. Defaults
   * to "cmcp" — host apps that don't want to share storage with a
   * CongressMCP install pass their own namespace (e.g. "donor-nexus",
   * "manresa") to avoid cross-app collisions.
   */
  storageNamespace?: string;
}

export function TaskChecklist({ storageNamespace }: TaskChecklistProps = {}) {
  const ns = storageNamespace ?? DEFAULT_STORAGE_NAMESPACE;
  const [plans, setPlans] = useState<PlanState[]>(() => loadPlans(ns));
  const [collapsed, setCollapsed] = useState<boolean>(() => loadPlans(ns).length === 0);
  const [muted, setMuted] = useState<boolean>(() => {
    try { return localStorage.getItem(mutedKey(ns)) === "1"; } catch { return false; }
  });
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track viewport so we can suppress auto-expand on mobile (where the
  // expanded panel would cover most of the screen). On md+ behavior is
  // unchanged.
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 767px)").matches
      : false,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const handler = () => setIsMobile(mq.matches);
    handler();
    mq.addEventListener("change", handler);
    window.addEventListener("resize", handler);
    return () => {
      mq.removeEventListener("change", handler);
      window.removeEventListener("resize", handler);
    };
  }, []);

  // Persist plans on every change.
  useEffect(() => { savePlans(ns, plans); }, [ns, plans]);

  // Persist mute pref.
  useEffect(() => {
    try { localStorage.setItem(mutedKey(ns), muted ? "1" : "0"); } catch { /* */ }
  }, [ns, muted]);

  // Auto-collapse N seconds after every plan in view is fully complete.
  useEffect(() => {
    if (collapseTimer.current) clearTimeout(collapseTimer.current);
    if (plans.length > 0 && plans.every(isComplete)) {
      collapseTimer.current = setTimeout(() => setCollapsed(true), AUTO_COLLAPSE_DELAY_MS);
    }
    return () => {
      if (collapseTimer.current) clearTimeout(collapseTimer.current);
    };
  }, [plans]);

  // Subscribe to plan events
  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<PlanEventDetail>).detail;
      if (!detail) return;

      if (detail.kind === "propose_plan") {
        const p = detail.input as PlanProposal;
        if (!p?.title || !Array.isArray(p?.steps)) return;
        const newPlan: PlanState = {
          id: genId(),
          title: p.title,
          affects: p.affects,
          reversible: p.reversible,
          shape: p.shape,
          entities: p.entities,
          steps: p.steps.map((label, i) => ({
            index: i,
            label,
            status: "pending",
          })),
          proposedAt: Date.now(),
        };
        setPlans((prev) => {
          // Cap at 5 visible plans — older ones drop off.
          const next = [newPlan, ...prev].slice(0, 5);
          return next;
        });
        // Don't auto-expand on mobile — the panel would cover most of
        // the viewport. The collapsed thermometer is the mobile UI.
        if (!muted && !isMobile) setCollapsed(false);
      }

      else if (detail.kind === "approve_plan") {
        // The user said "proceed" / "approved" — stamp the most recent
        // pending plan with approvedAt so the UI shows consent timestamp.
        setPlans((prev) => {
          if (prev.length === 0) return prev;
          const [head, ...rest] = prev;
          if (head.approvedAt) return prev; // already stamped
          return [{ ...head, approvedAt: Date.now() }, ...rest];
        });
      }

      else if (detail.kind === "update_plan_step") {
        const u = detail.input as PlanStepUpdate;
        if (typeof u?.step_index !== "number" || !u?.status) return;
        // Apply to the most-recent plan that has this step index in
        // a non-terminal state. update_plan_step doesn't carry plan
        // id (the tool comment says "the most recently proposed plan
        // card") so we mirror that semantics.
        setPlans((prev) => {
          if (prev.length === 0) return prev;
          const target = prev.findIndex((p) =>
            p.steps[u.step_index] && p.steps[u.step_index].status !== "success" && p.steps[u.step_index].status !== "failed"
          );
          if (target === -1) return prev;
          const updated = prev.map((p, i) => {
            if (i !== target) return p;
            return {
              ...p,
              steps: p.steps.map((s) => {
                if (s.index !== u.step_index) return s;
                return {
                  ...s,
                  status: u.status,
                  resultSummary: u.result_summary,
                  startedAt: s.startedAt ?? (u.status === "running" ? Date.now() : undefined),
                  endedAt: (u.status === "success" || u.status === "failed" || u.status === "skipped") ? Date.now() : s.endedAt,
                };
              }),
              completedAt: undefined, // recompute below
            };
          });
          return updated.map((p) => isComplete(p) && !p.completedAt ? { ...p, completedAt: Date.now() } : p);
        });
      }

      else if (detail.kind === "clear_plans") {
        setPlans([]);
        setCollapsed(true);
      }
    };
    window.addEventListener(PLAN_EVENT, handler);
    return () => window.removeEventListener(PLAN_EVENT, handler);
  }, [muted]);

  // Don't render at all if no plans AND user isn't expanding to view
  // history. (The collapsed tab is sticky once it's been earned, so
  // the user can re-open recently-completed plans.)
  if (plans.length === 0) return null;

  if (collapsed) {
    // Aggregate progress across ALL plans — thermometer reflects every
    // piece of passive AI work in flight, not just the latest plan.
    const totalSteps = plans.reduce((sum, p) => sum + p.steps.length, 0);
    const doneSteps = plans.reduce(
      (sum, p) => sum + p.steps.filter((s) => s.status === "success").length,
      0,
    );
    const failedSteps = plans.reduce(
      (sum, p) => sum + p.steps.filter((s) => s.status === "failed").length,
      0,
    );
    const activeCount = plans.filter((p) => !isComplete(p)).length;
    const allDone = activeCount === 0 && plans.length > 0;
    const anyFailed = failedSteps > 0;
    const pct =
      totalSteps > 0
        ? Math.min(100, Math.round((doneSteps / totalSteps) * 100))
        : 0;
    const latestPlan = plans.find((p) => !isComplete(p)) ?? plans[0];

    // Honest state — most plans the user sees stuck at 0/N are
    // actually awaiting approval, NOT running. Distinguish the two so
    // the operator knows whether to act vs wait. "running" requires
    // an approvedAt + at least one step that's left the pending state
    // OR an explicit running step.
    const awaitingApproval = plans.filter(
      (p) => !p.approvedAt && !isComplete(p),
    ).length;
    const actuallyRunning = plans.filter(
      (p) =>
        !!p.approvedAt &&
        !isComplete(p) &&
        p.steps.some((s) => s.status === "running" || s.status === "success"),
    ).length;
    // Approved but with no step activity yet = stalled (AI hasn't called
    // update_plan_step). After 5 minutes flag it as truly stalled.
    const STALLED_THRESHOLD_MS = 5 * 60 * 1000;
    const stalled = plans.filter((p) => {
      if (!p.approvedAt || isComplete(p)) return false;
      const anyActivity = p.steps.some(
        (s) => s.status !== "pending",
      );
      if (anyActivity) return false;
      return Date.now() - p.approvedAt > STALLED_THRESHOLD_MS;
    }).length;

    const state: "done" | "failed" | "running" | "awaiting" | "stalled" =
      allDone
        ? "done"
        : anyFailed
          ? "failed"
          : actuallyRunning > 0
            ? "running"
            : stalled > 0
              ? "stalled"
              : "awaiting";

    const dotClass =
      state === "failed"
        ? "bg-red-500"
        : state === "done"
          ? "bg-emerald-500"
          : state === "running"
            ? "bg-action animate-pulse"
            : state === "stalled"
              ? "bg-amber-500"
              : "bg-amber-400 animate-pulse";
    const fillClass =
      state === "failed"
        ? "bg-red-500"
        : state === "done"
          ? "bg-emerald-500"
          : state === "stalled"
            ? "bg-amber-500"
            : state === "awaiting"
              ? "bg-amber-400"
              : "bg-action";
    const labelText =
      state === "failed"
        ? `${failedSteps} failed`
        : state === "done"
          ? "Done"
          : state === "running"
            ? `${actuallyRunning} running`
            : state === "stalled"
              ? `${stalled} stalled`
              : `${awaitingApproval} awaiting`;
    const labelClass =
      state === "failed"
        ? "text-red-700 font-semibold"
        : state === "done"
          ? "text-emerald-700 font-semibold"
          : state === "stalled" || state === "awaiting"
            ? "text-amber-800 font-medium"
            : "text-stone-700";

    return (
      <button
        onClick={() => setCollapsed(false)}
        className="fixed top-20 right-0 z-40 bg-white border border-r-0 border-stone-200 rounded-l-lg shadow-md hover:shadow-lg transition-shadow px-2.5 py-1.5 flex items-center gap-2 max-w-[260px]"
        title={`${latestPlan.title} · ${doneSteps}/${totalSteps} steps`}
        aria-label={`AI work · ${labelText} · ${doneSteps} of ${totalSteps} steps`}
      >
        <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${dotClass}`} />
        {/* Thermometer — fills left→right as steps complete. The full
            bar locks emerald (or red on failure) and the label flips to
            "Done" so the operator can glance and know it's safe to move
            on without expanding the panel. */}
        <span
          className="relative inline-block w-14 h-1.5 rounded-full bg-stone-200 overflow-hidden flex-shrink-0"
          aria-hidden
        >
          <span
            className={`absolute inset-y-0 left-0 transition-all duration-300 ${fillClass}`}
            style={{ width: `${pct}%` }}
          />
        </span>
        <span className="text-[11px] font-mono tabular-nums text-stone-600 flex-shrink-0">
          {doneSteps}/{totalSteps}
        </span>
        <span className={`text-[11px] truncate ${labelClass}`}>
          {labelText}
        </span>
      </button>
    );
  }

  return (
    <div
      className={
        isMobile
          ? "fixed inset-x-3 bottom-3 top-auto max-h-[70vh] z-40 bg-white border border-stone-200 rounded-lg shadow-xl flex flex-col"
          : "fixed top-20 right-3 bottom-3 w-[340px] z-40 bg-white border border-stone-200 rounded-lg shadow-xl flex flex-col"
      }
    >
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-stone-200 flex items-center gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-stone-700 flex-1">
          AI work
          <span className="ml-1.5 font-normal text-stone-400 normal-case tracking-normal">
            {plans.filter((p) => !isComplete(p)).length > 0
              ? `· ${plans.filter((p) => !isComplete(p)).length} active`
              : `· ${plans.length} done`}
          </span>
        </div>
        <button
          onClick={() => setMuted((m) => !m)}
          className={
            "text-[10px] px-1.5 py-0.5 rounded border " +
            (muted ? "border-amber-300 bg-amber-50 text-amber-800" : "border-stone-200 text-stone-500 hover:bg-stone-50")
          }
          title={muted ? "Auto-open is muted — click to re-enable" : "Mute auto-open"}
        >
          {muted ? "muted" : "mute"}
        </button>
        <button
          onClick={() => setCollapsed(true)}
          className="text-stone-400 hover:text-stone-700 text-sm leading-none w-5 h-5 flex items-center justify-center"
          title="Collapse to tab"
        >×</button>
      </div>

      {/* Plans (most recent first) */}
      <div className="flex-1 overflow-y-auto">
        {plans.map((plan) => <PlanCard key={plan.id} plan={plan} onDismiss={() => setPlans((prev) => prev.filter((p) => p.id !== plan.id))} />)}
      </div>

      {/* Footer */}
      <div className="px-3 py-1.5 border-t border-stone-100 flex items-center gap-2">
        <span className="text-[10px] text-stone-400">
          Your AI client is driving this plan.
        </span>
        <button
          onClick={() => setPlans([])}
          className="ml-auto text-[10px] text-stone-400 hover:text-red-600"
          title="Clear all completed plans"
        >clear</button>
      </div>
    </div>
  );
}

// ── Plan card ─────────────────────────────────────────────────────────

function PlanCard({ plan, onDismiss }: { plan: PlanState; onDismiss: () => void }) {
  const prog = planProgress(plan);
  const done = isComplete(plan);
  const failed = plan.steps.some((s) => s.status === "failed");
  const running = plan.steps.find((s) => s.status === "running");

  return (
    <div className={
      "border-b border-stone-100 last:border-b-0 px-3 py-3 " +
      (done && !failed ? "bg-emerald-50/40" : failed ? "bg-red-50/40" : running ? "bg-accent/10" : "")
    }>
      {/* Title + progress */}
      <div className="flex items-baseline gap-2 mb-1.5">
        <div className="text-sm font-semibold text-stone-900 flex-1 leading-snug">
          {plan.title}
        </div>
        <span className="text-[10px] font-mono tabular-nums text-stone-500 shrink-0">
          {prog.done}/{prog.total}
        </span>
        {done && (
          <button
            onClick={onDismiss}
            className="text-stone-300 hover:text-stone-600 w-4 h-4 flex items-center justify-center"
            title="Dismiss"
            aria-label="Dismiss"
          ><X size={12} aria-hidden /></button>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-stone-100 rounded-full overflow-hidden mb-2">
        <div
          className={
            "h-full transition-all duration-300 " +
            (failed ? "bg-red-500" : done ? "bg-emerald-500" : "bg-action")
          }
          style={{ width: `${prog.pct}%` }}
        />
      </div>

      {/* Scope chips */}
      <div className="flex flex-wrap gap-1 mb-2 text-[10px]">
        {plan.affects && (
          <span className="px-1.5 py-0.5 rounded bg-stone-100 text-stone-700">
            {plan.affects}
          </span>
        )}
        {plan.reversible === true && (
          <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100">
            reversible
          </span>
        )}
        {plan.reversible === false && (
          <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-100">
            not easily reversible
          </span>
        )}
        {plan.approvedAt && (
          <span className="px-1.5 py-0.5 rounded bg-success-soft text-success border border-success/20 inline-flex items-center gap-0.5" title={new Date(plan.approvedAt).toLocaleString()}>
            <Check size={10} strokeWidth={2.5} aria-hidden /> approved {fmtClock(plan.approvedAt)}
          </span>
        )}
        {!plan.approvedAt && !done && !failed && (
          <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 font-medium">
            awaiting approval
          </span>
        )}
      </div>

      {/* Entity chips */}
      {plan.entities && plan.entities.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2 text-[10px]">
          {plan.entities.slice(0, 8).map((e, i) => (
            <span key={`${e.id ?? e.name}-${i}`} className="px-1.5 py-0.5 rounded bg-stone-50 border border-stone-200 text-stone-700">
              {e.name}
              {e.type && <span className="text-stone-400 ml-1">· {e.type}</span>}
            </span>
          ))}
          {plan.entities.length > 8 && (
            <span className="px-1.5 py-0.5 text-stone-400">+{plan.entities.length - 8} more</span>
          )}
        </div>
      )}

      {/* Steps */}
      <ol className="space-y-1">
        {plan.steps.map((s) => <StepRow key={s.index} step={s} />)}
      </ol>
    </div>
  );
}

function StepRow({ step }: { step: PlanStepState }) {
  const Icon: LucideIcon =
    step.status === "running" ? Loader2 :
    step.status === "success" ? Check :
    step.status === "failed" ? X :
    step.status === "skipped" ? Ban :
    Dot;
  const cls =
    step.status === "running" ? "text-action animate-spin" :
    step.status === "success" ? "text-emerald-700" :
    step.status === "failed" ? "text-red-700" :
    step.status === "skipped" ? "text-stone-400" :
    "text-stone-300";
  const labelCls =
    step.status === "success" ? "text-stone-700" :
    step.status === "failed" ? "text-red-900" :
    step.status === "running" ? "text-stone-900 font-medium" :
    step.status === "skipped" ? "text-stone-400 line-through" :
    "text-stone-600";
  return (
    <li className="flex items-start gap-2 text-xs leading-snug">
      <Icon size={12} strokeWidth={2.5} className={`shrink-0 mt-0.5 ${cls}`} aria-label={step.status} />
      <span className={`flex-1 ${labelCls}`}>
        {step.label}
        {step.resultSummary && (
          <span className="block text-[10px] text-stone-500 italic mt-0.5">
            {step.resultSummary}
          </span>
        )}
      </span>
    </li>
  );
}

function fmtClock(ts: number): string {
  const d = new Date(ts);
  const hh = d.getHours().toString().padStart(2, "0");
  const mm = d.getMinutes().toString().padStart(2, "0");
  return `${hh}:${mm}`;
}

// ── Public dispatcher helpers ────────────────────────────────────────
// Dispatch helpers now live in framework/react/plan-events. They're
// re-exported above for back-compat with existing callers.
